<script setup lang="ts">
import { computed, ref } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { markWelcomeSeen as markWelcomeSeenApi } from '@/features/users/api/usersApi';
import { getGettingStartedUrl } from '@/shared/utils/tutorialUrls';
import { getCurrentJurisdiction } from '@/features/startTask/preset_task/jurisdictionResolver';
import TutorialViewerDialog from '@/shared/components/TutorialViewerDialog.vue';

const sessionStore = useSessionStore();

// "오늘 하루 보지 않기" 스누즈 (localStorage). 영구 차단은 서버 플래그(welcomeSeen) 사용.
const SNOOZE_KEY = 'lettaga_welcome_snooze';
const todayStr = () => new Date().toISOString().slice(0, 10);
function isSnoozedToday(): boolean {
  try {
    return localStorage.getItem(SNOOZE_KEY) === todayStr();
  } catch {
    return false;
  }
}

// 같은 세션에서 닫은 직후 즉시 숨기기 위한 로컬 플래그
const dismissed = ref(false);

// 인증됨 + 서버 플래그 welcomeSeen === false + 오늘 스누즈 안 됨 일 때만 노출
const show = computed(() => {
  if (dismissed.value) return false;
  if (!sessionStore.getIsAuthenticated) return false;
  if (sessionStore.getUser?.user?.welcomeSeen !== false) return false;
  return !isSnoozedToday();
});

const viewerOpen = ref(false);
const viewerUrl = ref('');

const steps = [
  { n: 1, html: '<b>Set up your firm</b> · security, 2FA &amp; team' },
  { n: 2, html: '<b>Add the companies you manage</b> · register or migrate them in (Entity)' },
  { n: 3, html: '<b>Start filing</b> · Start Task → T-Sign → e-Filing' },
];

/** 영구 차단 — 서버 플래그(welcomeSeen='Y') + 세션 반영. */
async function persistSeen() {
  if (dismissed.value) return;
  dismissed.value = true;
  sessionStore.markWelcomeSeen();
  try {
    await markWelcomeSeenApi();
  } catch (e) {
    // 실패해도 UX 는 막지 않음. 다음 세션에서 다시 시도됨.
    console.warn('[WelcomeModal] markWelcomeSeen 실패', e);
  }
}

/** Take the 2-minute tour — Getting Started 열고 영구 차단. */
function onTakeTour() {
  viewerUrl.value = getGettingStartedUrl(getCurrentJurisdiction() || 'MY');
  viewerOpen.value = true;
  persistSeen();
}

/** I'll explore on my own — 영구 차단. */
function onSkip() {
  persistSeen();
}

/** Don't show today — 오늘 하루만 숨김(localStorage 스누즈). */
function onSnoozeToday() {
  try {
    localStorage.setItem(SNOOZE_KEY, todayStr());
  } catch {
    /* ignore */
  }
  dismissed.value = true;
}

/** Don't show again — 영구 차단. */
function onNeverShow() {
  persistSeen();
}
</script>

<template>
  <v-dialog
    :model-value="show"
    persistent
    max-width="432"
    content-class="welcome-dialog"
    @update:model-value="(v: boolean) => { if (!v) onSkip(); }"
  >
    <div class="welcome">
      <!-- top (gradient) -->
      <div class="wtop">
        <div class="emoji">👋</div>
        <h2>Welcome to Lettāga</h2>
        <p>Let's get your firm set up. A quick 2-minute tour walks you through the order to do things the first time.</p>
      </div>

      <!-- steps -->
      <div class="wbody">
        <div class="wsteps">
          <div v-for="step in steps" :key="step.n" class="wstep">
            <span class="num">{{ step.n }}</span>
            <span class="wl" v-html="step.html" />
          </div>
        </div>
      </div>

      <!-- actions -->
      <div class="wfoot">
        <button type="button" class="wbtn primary" @click="onTakeTour">
          ▶ Take the 2-minute tour
        </button>
        <button type="button" class="wbtn ghost" @click="onSkip">I'll explore on my own</button>
        <div class="whint">You can reopen the tour anytime from the <b>?</b> at the top-right.</div>
        <div class="wdismiss">
          <button type="button" class="wlink" @click="onSnoozeToday">Don't show today</button>
          <span class="sep">·</span>
          <button type="button" class="wlink" @click="onNeverShow">Don't show again</button>
        </div>
      </div>
    </div>
  </v-dialog>

  <TutorialViewerDialog v-model="viewerOpen" :url="viewerUrl" title="Getting started" />
</template>

<style scoped>
.welcome {
  width: 432px;
  max-width: 100%;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.wtop {
  background: linear-gradient(135deg, #1b84ff, #1b84ff);
  color: #fff;
  padding: 22px 24px;
}
.wtop .emoji {
  font-size: 30px;
  line-height: 1;
}
.wtop h2 {
  margin: 9px 0 4px;
  font-size: 20px;
  font-weight: 800;
}
.wtop p {
  margin: 0;
  font-size: 13px;
  color: #dce8ff;
  line-height: 1.55;
}
.wbody {
  padding: 18px 24px 4px;
}
.wsteps {
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.wstep {
  display: flex;
  align-items: center;
  gap: 12px;
}
.wstep .num {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #eaf2ff;
  color: #1b84ff;
  font-weight: 800;
  font-size: 12px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.wstep .wl {
  font-size: 13px;
  color: #46506a;
}
.wstep .wl :deep(b) {
  color: #3a4752;
}
.wfoot {
  padding: 16px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.wbtn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border-radius: 11px;
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
}
.wbtn.primary {
  background: #1b84ff;
  color: #fff;
}
.wbtn.primary:hover {
  background: #1670e0;
}
.wbtn.ghost {
  background: #fff;
  color: #768b9e;
  border: 1px solid #ebf1f6;
}
.wbtn.ghost:hover {
  border-color: #c7d2e6;
  color: #3a4752;
}
.whint {
  text-align: center;
  font-size: 11.5px;
  color: #9aa6bd;
  margin-top: 3px;
}
.whint b {
  color: #768b9e;
}
.wdismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 4px;
}
.wdismiss .wlink {
  background: none;
  border: none;
  color: #9aa6bd;
  font-size: 11.5px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 6px;
}
.wdismiss .wlink:hover {
  color: #768b9e;
  text-decoration: underline;
}
.wdismiss .sep {
  color: #cdd5e2;
  font-size: 11px;
}
</style>

<style>
/* v-dialog 기본 박스 그림자/배경 제거 — .welcome 카드 자체 스타일만 보이도록 */
.welcome-dialog {
  box-shadow: none;
  border-radius: 18px;
}
</style>
